import { has, isArray, isString, isNumber, every, isNull, get } from 'lodash';
import { DateTime } from 'luxon';
import { ValidationResult } from '../../models';

// dataObj: Using format 2 from https://api.highcharts.com/highcharts/series.line.data
// xAxisMin: number | null (https://api.highcharts.com/highcharts/xAxis.min)
// xAxisMax: number | null (https://api.highcharts.com/highcharts/xAxis.max)
export const isValidTimeSeriesData = (dataObj: any, xAxisMin?: any, xAxisMax?: any): ValidationResult => {
  let isDataObjValid = true;
  let isXAxisMinValid = true;
  let isXAxisMaxValid = true;
  const errors: string[] = [];

  if (dataObj == undefined) {
    isDataObjValid = false;
    errors.push('Series is not defined');
    return { isValid: isDataObjValid, errors };
  }

  if (!isArray(dataObj)) {
    isDataObjValid = false;
    errors.push(`Series should be an array, but is of type ${typeof dataObj}`);
    return { isValid: isDataObjValid, errors };
  }

  isDataObjValid = every(dataObj, seriesObj => {
    let isSeriesValid = true;

    // Check that each series is an object with 'data' property
    if (!has(seriesObj, 'data')) {
      isSeriesValid = false;
      errors.push("Each series should be an object with a 'data' property");
    }

    // If 'data' exists, check if it is an array
    if (get(seriesObj, 'data') && !isArray(seriesObj.data)) {
      isSeriesValid = false;
      errors.push(`Data should be an array, but is of type ${typeof seriesObj.data}`);
    }

    if (get(seriesObj, 'data') && seriesObj.data.length > 0) {
      isSeriesValid = every(seriesObj.data, dataPoint => {
        let isPointValid = true;
        if (!isArray(dataPoint)) {
          // Check that each point is entered as an array
          isPointValid = false;
          errors.push(`Data point should be an array, but is of type ${typeof dataPoint}`);
        } else if (dataPoint.length < 2) {
          // Check that each point is entered as an array of at least 2 items
          isPointValid = false;
          errors.push(`Data point should be an array of at least 2 values, but contains only ${dataPoint.length}`);
        } else {
          if (
            // Check if the first element of the point is a string or number
            (!isString(dataPoint[0]) && !isNumber(dataPoint[0])) ||
            // If the first element of the point is a string, check if the string is a valid ISO string
            (isString(dataPoint[0]) && !DateTime.fromISO(dataPoint[0]).isValid) ||
            // If the first element of the point is a number, check if the number is a valid epoch in ms
            (isNumber(dataPoint[0]) && !DateTime.fromMillis(dataPoint[0]).isValid)
          ) {
            isPointValid = false;
            errors.push(
              `First item in data point should be a valid timestamp in ISO format or epoch ms, but is of type ${typeof dataPoint[0]}`,
            );
          }

          // Check if the rest of the elements of the point are numbers or null
          if (!every(dataPoint.slice(1), item => isNumber(item) || isNull(item))) {
            isPointValid = false;
            errors.push('Subsequent items in data point should be a number or null');
          }
        }
        return isPointValid;
      });
    }

    // Check if 'name' property, if it exists, is a string
    if (has(seriesObj, 'name') && !isString(seriesObj.name)) {
      isSeriesValid = false;
      errors.push(`Name should be a string, but is of type ${typeof seriesObj.name}`);
    }

    return isSeriesValid;
  });

  if (xAxisMin != null) {
    if (!isNumber(xAxisMin)) {
      isXAxisMinValid = false;
      errors.push(`X-axis start time should be a number, but is of type ${typeof xAxisMin}`);
    }
    if (isNumber(xAxisMin) && !DateTime.fromMillis(xAxisMin).isValid) {
      isXAxisMinValid = false;
      errors.push(`X-axis start time should be a number in epoch ms, but received ${xAxisMin}`);
    }
  }

  if (xAxisMax != null) {
    if (!isNumber(xAxisMax)) {
      isXAxisMaxValid = false;
      errors.push(`X-axis end time should be a number, but is of type ${typeof xAxisMax}`);
    }
    if (isNumber(xAxisMax) && !DateTime.fromMillis(xAxisMax).isValid) {
      isXAxisMaxValid = false;
      errors.push(`X-axis end time should be a number in epoch ms, but received ${xAxisMax}`);
    }
  }

  if (isNumber(xAxisMin) && isNumber(xAxisMax)) {
    if (xAxisMax <= xAxisMin) {
      isXAxisMaxValid = false;
      errors.push('X-axis end time should be after x-axis start time');
    }
  }

  if (errors.length > 0) {
    console.error('Error with time series data: ', errors);
  }

  return { isValid: isDataObjValid && isXAxisMinValid && isXAxisMaxValid, errors };
};
